import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import FullPageSpinner from '../../FullPageSpinner';
import { RESTAURANT_BACKEND_HOST, apiCall } from '../RestaurantBackend';
import {
  getShift,
  getShiftTranscript,
  getShiftEvents,
  getShiftInteractions,
  getShiftGemTransactions,
  getShiftPosOrders,
} from '../../../services/scopedReadsService';
import { FaPlay, FaPause, FaChartBar, FaCalendarAlt, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useTenant } from '../../../contexts/TenantContext';
import { parseEmojiString } from '../../../utils/metricUtils';
import { getVersion, getTemplate, createTestCase } from '../../../services/promptManagementService';
import { MetricDefinition } from '../../../types/prompt';
import { visibleShiftEvents } from './shiftEventFilters';
import ShiftRolePromptRow from './ShiftRolePromptRow';

interface TranscriptPart {
  id: string;
  start_time: number;
  text: string;
  speaker: string;
  created: string;
}

// Add ShiftEvent interface
interface ShiftEvent {
  id: string;
  event_type: string;
  event_data: any;
  created: string;
}

// Create a combined item type for sorting transcripts and events together
interface TimelineItem {
  type: 'transcript' | 'event' | 'order';
  data: TranscriptPart | ShiftEvent | RevelOrder;
  created: string;
}

interface Recording {
  url: string;
  key: string;
  size: number;
  last_modified: string;
  duration?: number;
}

interface ShiftInteraction {
  id: string;
  start_time: number;
  end_time: number;
  interaction_type: string;
  data: any;
  created: string;
  correct_human_verified: boolean | null;
}

// Interaction types shown by default in the transcript interaction lists. Anything
// NOT in this allowlist (phrase_rule, drink_promotion, goodbye, missed_upsell,
// garnish_complete, and any unknown/future type) is hidden until the reviewer checks
// "Show all interaction types". Allowlist semantics — new/unknown types stay hidden by
// default. See spec feat-154 Part 2. This is a display filter only; it must not affect
// the Performance Summary metric math.
const DEFAULT_VISIBLE_INTERACTION_TYPES = new Set<string>([
  'greeting',
  'initial_greeting',
  'greeter_greeting',
  'server_upsell',
  'greeter_upsell',
  'cashier_upsell',
]);

interface ServiceCriterion {
  type: string;
  fulfilled: boolean;
}

interface RevelOrderItemInteractionLink {
  shift_interaction_id: string;
  match_rule: string;       // drink_promotion, metric_1, metric_2, metric_3
  time_diff_seconds: number;
  match_explanation: string | null;
}

interface RevelOrderItem {
  id: number;
  item_name: string;
  price: number | null;
  quantity: number;
  interaction_links: RevelOrderItemInteractionLink[];
}

interface RevelOrder {
  order_no: number;
  shift_offset_seconds: number;
  transcript_part_id: string | null;
  first_opened: string | null;
  last_closed: string | null;
  final_total: number;
  net_sales: number;
  items: RevelOrderItem[];
}

interface ShiftDetails {
  serverName: string;
  shiftCreated: string;
  shiftEnded: string | null;
  promptTemplateVersionId?: string | null;
  role?: string | null;
  // feat-319 — Role | Prompt header row (all nullable; see ShiftRolePromptRow).
  serverDefaultRole?: string | null;
  effectiveRole?: string | null;
  roleDisplayName?: string | null;
  promptTemplateName?: string | null;
  promptTemplateRole?: string | null;
  promptTemplateTenantId?: string | null;
  promptVersionNumber?: number | string | null;
  promptVersionIsActive?: boolean | null;
  // App version snapshot — frozen at shift creation time, never updated
  // by later device upgrades. Source: shift.app_version etc. on the
  // shift row. Pre-existing shifts predate the snapshot and have NULLs.
  shiftAppVersion?: string | null;
  shiftAppBuild?: string | null;
  shiftPatchNumber?: number | null;
  shiftDeviceModel?: string | null;
  // Device's manual name (e.g. "Preston's Phone"). Read live from
  // device_info; renames are infrequent and not version-sensitive, so
  // showing the latest name on a historical shift is OK.
  deviceName?: string | null;
  deviceInfo?: {
    client_type: string;
    device_name?: string;
    device_model?: string;
    system_name?: string;
    system_version?: string;
    app_version?: string;
  };
}

// Add the CriteriaStats interface from ShiftPerformanceView
interface CriteriaStats {
  criterionType: string;
  fulfilled: number;
  total: number;
  percentage: number;
}

// Mirrors the SummaryJson shape produced by services/shift_summarizer.py
// and consumed by the Flutter post-shift screen (lib/models/shift_summary.dart).
interface SummaryHighlight {
  title: string;
  quote?: string;
  reasoning?: string;
  interaction_id?: string;
  interaction_type?: string;
}

interface SummaryArea {
  title: string;
  quote?: string;
  reasoning?: string;
  suggestion?: string;
  interaction_id?: string;
  source?: string;
}

interface ShiftSummaryJson {
  highlights?: SummaryHighlight[];
  areas_to_improve?: SummaryArea[];
}

const ShiftTranscriptView: React.FC = () => {
  const { shiftId, serverId } = useParams<{ shiftId: string; serverId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Deep-link: `?t=<seconds-from-shift-start>` scrolls to and highlights the
  // nearest transcript line. Used by the side-by-side cross-talk comparison view
  // so each pane lands on the bled phrase.
  const [highlightedStartTime, setHighlightedStartTime] = useState<number | null>(null);
  const didDeepLinkScroll = useRef(false);
  const { metricLabels, fetchVersionConfigs, getConfigForShift } = useTenant();
  const [transcriptParts, setTranscriptParts] = useState<TranscriptPart[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [interactions, setInteractions] = useState<ShiftInteraction[]>([]);
  // Add state for shift events
  const [shiftEvents, setShiftEvents] = useState<ShiftEvent[]>([]);
  // Add state for combined timeline items
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [revelOrders, setRevelOrders] = useState<RevelOrder[]>([]);
  const [gemByInteractionId, setGemByInteractionId] = useState<Record<string, { amount: number; description: string | null }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentRecordingIndex, setCurrentRecordingIndex] = useState(0);
  const [shiftDetails, setShiftDetails] = useState<ShiftDetails | null>(null);
  const [shiftSummary, setShiftSummary] = useState<ShiftSummaryJson | null>(null);
  const [isCreatingTestCase, setIsCreatingTestCase] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(() => {
    const stored = localStorage.getItem('shiftTranscriptView.showVerifiedOnly');
    return stored === null ? true : stored === 'true';
  });
  const [showEvents, setShowEvents] = useState(() => {
    const stored = localStorage.getItem('shiftTranscriptView.showEvents');
    return stored === null ? true : stored === 'true';
  });
  const [showPerformanceDetails, setShowPerformanceDetails] = useState(() => {
    const stored = localStorage.getItem('shiftTranscriptView.showPerformanceDetails');
    return stored === 'true';
  });
  // Session-only display filter for the interaction lists (inline timeline rows +
  // summary table). Intentionally NOT persisted: resets to unchecked on each fresh
  // page visit. See spec feat-154 Part 2.
  const [showAllInteractionTypes, setShowAllInteractionTypes] = useState(false);
  const [isReportingBug, setIsReportingBug] = useState(false);

  useEffect(() => {
    localStorage.setItem('shiftTranscriptView.showPerformanceDetails', String(showPerformanceDetails));
  }, [showPerformanceDetails]);

  useEffect(() => {
    localStorage.setItem('shiftTranscriptView.showVerifiedOnly', String(showVerifiedOnly));
  }, [showVerifiedOnly]);

  useEffect(() => {
    localStorage.setItem('shiftTranscriptView.showEvents', String(showEvents));
  }, [showEvents]);

  // New states for audio loading
  const [audioLoadingState, setAudioLoadingState] = useState<'idle' | 'loading' | 'complete'>('idle');
  const [audioLoadingProgress, setAudioLoadingProgress] = useState(0);
  const [totalAudioDuration, setTotalAudioDuration] = useState(0);
  const [audioDurationMap, setAudioDurationMap] = useState<{ [startTime: number]: { fileIndex: number, offsetInFile: number } }>({});
  const [currentTime, setCurrentTime] = useState(0);
  
  // State for audio combination
  const [isCombiningAudio, setIsCombiningAudio] = useState(false);
  const [combineAudioStatus, setCombineAudioStatus] = useState<string | null>(null);

  // State for full.wav download
  const [isDownloadingFullWav, setIsDownloadingFullWav] = useState(false);
  
  // Check if we have a combined file (seamless playback)
  const isCombinedFile = recordings.length === 1 && recordings[0]?.key?.includes('full.');
  
  // Check if we have chunks but no combined file
  const hasChunksButNoCombined = recordings.length > 1 || (recordings.length > 0 && !isCombinedFile);

  // Hidden audio element for measuring duration
  const hiddenAudioRef = useRef<HTMLAudioElement | null>(null);
  
  // Ref to track if combined file metadata has loaded (for timeout detection)
  const combinedFileLoadedRef = useRef(false);

  // Get metric config for this shift (from prompt template version or role fallback)
  const shiftMetricConfig = shiftDetails ? getConfigForShift({
    prompt_template_version_id: shiftDetails.promptTemplateVersionId ?? undefined,
    role: shiftDetails.role,
  }) : null;

  // Build criteria types and labels dynamically from metric config
  const criteriaTypes: string[] = shiftMetricConfig
    ? shiftMetricConfig.metrics.map(m => m.key)
    : ["metric_1", "metric_2", "metric_3"];

  const criteriaLabels: Record<string, string> = shiftMetricConfig
    ? Object.fromEntries([
        ...shiftMetricConfig.metrics.map(m => [m.key, `${m.label} Upsell`]),
        ...(shiftMetricConfig.drink_promotion?.enabled
          ? [["drink_promotion", shiftMetricConfig.drink_promotion.label || "Drink Promotion"]]
          : [["drink_promotion", metricLabels.drink_promotion]]),
      ])
    : {
        "metric_1": `${metricLabels.metric_1} Upsell`,
        "metric_2": `${metricLabels.metric_2} Upsell`,
        "metric_3": `${metricLabels.metric_3} Upsell`,
        "drink_promotion": metricLabels.drink_promotion,
      };

  // Build emoji map from metric config (decode legacy uD83E... format)
  const criteriaEmojiMap: Record<string, string> = shiftMetricConfig
    ? Object.fromEntries(shiftMetricConfig.metrics.map(m => [m.key, m.emoji ? parseEmojiString(m.emoji) : '\u2022']))
    : {
        "metric_1": "\uD83E\uDD69",
        "metric_2": "\uD83C\uDF56",
        "metric_3": "\uD83C\uDF6B",
      };

  // Lookup of full metric definitions by key (label/description live here),
  // sourced from the same per-shift prompt config — single source, no re-derive.
  const metricDefByKey: Record<string, MetricDefinition> = shiftMetricConfig
    ? Object.fromEntries(shiftMetricConfig.metrics.map(m => [m.key, m]))
    : {};

  // Shared id for the per-metric-icon hover tooltip in the interaction rows.
  const metricTooltipId = 'transcript-metric-tooltip';

  // Calculate first transcript part ID for each interaction
  const getFirstTranscriptPartForInteraction = (interaction: ShiftInteraction): string | null => {
    const matchingParts = transcriptParts.filter(part =>
      part.start_time >= interaction.start_time &&
      part.start_time <= interaction.end_time
    );
    return matchingParts.length > 0 ? matchingParts[0].id : null;
  };

  const buildFeedbackDisplay = (data: any): React.ReactNode => {
    const serviceCriteria = (data?.service_criteria || []) as ServiceCriterion[];
    const criteriaStatus = Object.fromEntries(
      serviceCriteria.map((criterion: ServiceCriterion) => [criterion.type, criterion.fulfilled || false])
    );

    // Use all criteria present in the data, ordered by the metric config
    // This handles any number of metrics dynamically
    const presentKeys = serviceCriteria.map(c => c.type);
    const orderedKeys = criteriaTypes.filter(k => presentKeys.includes(k));
    // Include any keys from data not in our config (forward compat)
    const extraKeys = presentKeys.filter(k => !orderedKeys.includes(k));
    const allKeys = [...orderedKeys, ...extraKeys];

    // One hoverable element per metric so each icon can carry its own tooltip
    // (label + description + status). Content is resolved lazily by the shared
    // <Tooltip> instance from the per-shift config \u2014 see metricTooltipId.
    return allKeys.map((key, idx) => {
      const emoji = criteriaEmojiMap[key] || "\u2022";
      const fulfilled = !!criteriaStatus[key];
      const status = fulfilled ? "\u2705" : "\u2796";
      return (
        <span
          key={key}
          data-tooltip-id={metricTooltipId}
          data-tooltip-content={JSON.stringify({ key, fulfilled })}
          className="cursor-help"
          style={{ marginRight: idx < allKeys.length - 1 ? '0.5rem' : undefined }}
        >
          {emoji}{status}
        </span>
      );
    });
  };

  const formatTimestamp = (seconds: number): string => {
    return `${seconds.toFixed(1)}s`;
  };

  // Group flat SQL rows (one per item×link) into nested RevelOrder objects
  const groupOrderRows = (rows: any[]): RevelOrder[] => {
    const orderMap = new Map<number, RevelOrder>();

    for (const row of rows) {
      const orderNo = row.order_no;

      if (!orderMap.has(orderNo)) {
        orderMap.set(orderNo, {
          order_no: orderNo,
          shift_offset_seconds: parseFloat(row.shift_offset_seconds),
          transcript_part_id: row.transcript_part_id || null,
          first_opened: row.first_opened || null,
          last_closed: row.last_closed || null,
          final_total: parseFloat(row.final_total) || 0,
          net_sales: parseFloat(row.net_sales) || 0,
          items: [],
        });
      }

      const order = orderMap.get(orderNo)!;

      if (row.item_id != null) {
        // Check if this item already exists (we may have multiple link rows per item)
        let item = order.items.find(i => i.id === row.item_id);
        if (!item) {
          item = {
            id: row.item_id,
            item_name: row.item_name || 'Unknown item',
            price: row.price != null ? parseFloat(row.price) : null,
            quantity: row.quantity != null ? parseInt(row.quantity, 10) : 1,
            interaction_links: [],
          };
          order.items.push(item);
        }

        if (row.shift_interaction_id) {
          item.interaction_links.push({
            shift_interaction_id: row.shift_interaction_id,
            match_rule: row.match_rule,
            time_diff_seconds: parseFloat(row.time_diff_seconds) || 0,
            match_explanation: row.match_explanation || null,
          });
        }
      }
    }

    return Array.from(orderMap.values());
  };

  // Display-only allowlist filter for the interaction lists. Honors the
  // "Show all interaction types" toggle. Does NOT touch metric math.
  const isInteractionTypeVisible = (interaction: ShiftInteraction): boolean => {
    return showAllInteractionTypes ||
      DEFAULT_VISIBLE_INTERACTION_TYPES.has(interaction.interaction_type);
  };

  const isTimeInAnyInteraction = (time: number): boolean => {
    return interactions.some(
      interaction =>
        isInteractionTypeVisible(interaction) &&
        time >= interaction.start_time &&
        time <= interaction.end_time
    );
  };

  const getInteractionsForTime = (time: number): ShiftInteraction[] => {
    return interactions.filter(
      interaction =>
        isInteractionTypeVisible(interaction) &&
        time >= interaction.start_time &&
        time <= interaction.end_time
    );
  };

  const getNextInteraction = (currentStartTime: number): ShiftInteraction | null => {
    const sortedInteractions = [...interactions].sort((a, b) => a.start_time - b.start_time);
    const nextInteraction = sortedInteractions.find(interaction => interaction.start_time > currentStartTime);
    return nextInteraction || null;
  };

  const handleNextInteraction = (currentStartTime: number) => {
    const nextInteraction = getNextInteraction(currentStartTime);
    if (nextInteraction) {
      const element = document.querySelector(`[data-time="${nextInteraction.start_time}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (audioRef.current) {
        audioRef.current.currentTime = nextInteraction.start_time;
      }
    }
  };

  // Deep-link scroll: once transcripts are loaded, jump to the line nearest the
  // `?t=` second-offset and highlight it. Fires once per mount.
  useEffect(() => {
    if (isLoading || didDeepLinkScroll.current) return;
    const tRaw = searchParams.get('t');
    if (tRaw === null || transcriptParts.length === 0) return;
    const t = parseFloat(tRaw);
    if (Number.isNaN(t)) return;
    let nearest = transcriptParts[0];
    for (const part of transcriptParts) {
      if (Math.abs(part.start_time - t) < Math.abs(nearest.start_time - t)) {
        nearest = part;
      }
    }
    didDeepLinkScroll.current = true;
    setHighlightedStartTime(nearest.start_time);
    // wait one tick for the highlight class to render, then scroll
    setTimeout(() => {
      const element = document.querySelector(`[data-time="${nearest.start_time}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }, [isLoading, transcriptParts, searchParams]);

  // Function to load and measure audio durations
  const loadAudioDurations = async () => {
    if (!recordings.length) {
      console.log('No recordings to load');
      return;
    }

    // For very large numbers of files, warn the user and limit loading
    const MAX_FILES_TO_LOAD = 1000; // Limit to prevent browser/server overload
    const shouldLimitFiles = recordings.length > MAX_FILES_TO_LOAD;
    
    if (shouldLimitFiles) {
      console.warn(`⚠️ Too many audio files (${recordings.length}). This may cause performance issues.`);
      console.warn(`   Limiting to first ${MAX_FILES_TO_LOAD} files. Consider waiting for combined file.`);
      console.warn(`   A combined audio file is being generated in the background.`);
    }

    const filesToLoad = shouldLimitFiles ? recordings.slice(0, MAX_FILES_TO_LOAD) : recordings;
    console.log(`Starting to load ${filesToLoad.length} audio files (out of ${recordings.length} total)...`);
    setAudioLoadingState('loading');
    setAudioLoadingProgress(0); // Initialize to 0

    // Create a hidden audio element if it doesn't exist
    if (!hiddenAudioRef.current) {
      const audio = document.createElement('audio');
      audio.style.display = 'none'; // Keep it hidden
      // Use a unique ID or other method if multiple instances might exist
      audio.id = `hidden-audio-measure-${shiftId}`;
      // Append somewhere stable, like the body, but ensure cleanup
      document.body.appendChild(audio);
      hiddenAudioRef.current = audio;
    }

    const updatedRecordings = [...recordings];
    const measuredDurations: (number | null)[] = new Array(updatedRecordings.length).fill(null);
    const errorDetails: Array<{ index: number; key: string; url: string; error: string }> = [];
    
    // Adaptive batch size: smaller for large file counts to avoid overwhelming the server
    const batchSize = recordings.length > 1000 ? 3 : recordings.length > 100 ? 5 : 10;
    const batchDelay = recordings.length > 1000 ? 200 : recordings.length > 100 ? 100 : 50; // ms delay between batches
    
    let filesProcessed = 0;
    let filesCompleted = 0;

    // Update progress function that ensures it always increases
    const updateProgress = () => {
      const currentProgress = Math.round((filesCompleted / filesToLoad.length) * 100);
      setAudioLoadingProgress(Math.max(1, currentProgress)); // Ensure at least 1% to show activity
    };

    // Initialize progress to show loading has started
    updateProgress();

    // 1. Measure durations in batches
    for (let i = 0; i < filesToLoad.length; i += batchSize) {
      const batch = filesToLoad.slice(i, i + batchSize);
      const batchNum = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(filesToLoad.length / batchSize);
      console.log(`Processing batch ${batchNum}/${totalBatches}: files ${i} to ${Math.min(i + batchSize - 1, filesToLoad.length - 1)}`);
      
      const batchPromises = batch.map((recording, indexInBatch) => {
        // Create a *new* audio element for each concurrent request
        const tempAudio = document.createElement('audio');
        const originalIndex = i + indexInBatch;
        filesProcessed++;
        // Suppress stalled logs after first 20 files to reduce console spam
        const suppressStalledLogs = originalIndex >= 20;
        
        return getAudioDuration(recording.url, tempAudio, suppressStalledLogs) // Pass the element
          .then(duration => {
            // Only log every 10th file to reduce console spam
            if (originalIndex % 10 === 0 || originalIndex < 10) {
              console.log(`✓ Audio file ${originalIndex + 1}/${filesToLoad.length}: ${recording.key}, Duration: ${duration}s`);
            }
            updatedRecordings[originalIndex].duration = duration;
            measuredDurations[originalIndex] = duration;
            filesCompleted++;
            updateProgress(); // Update progress as each file completes
          })
          .catch(err => {
            const errorMessage = err instanceof Error ? err.message : String(err);
            // Only log errors for first 20 files to reduce console spam
            if (errorDetails.length < 20) {
              console.error(`✗ Error loading audio file ${originalIndex + 1}/${filesToLoad.length} (${recording.key}):`, errorMessage);
              console.error(`  Failed URL: ${recording.url.substring(0, 100)}...`);
            }
            errorDetails.push({
              index: originalIndex,
              key: recording.key,
              url: recording.url,
              error: errorMessage
            });
            measuredDurations[originalIndex] = null; // Keep as null to track failures
            filesCompleted++; // Count failures too for progress
            updateProgress(); // Update progress even on failure
          });
      });

      // Wait for the current batch to complete
      await Promise.all(batchPromises);
      const progressPercent = Math.round((filesCompleted / filesToLoad.length) * 100);
      console.log(`Batch ${batchNum}/${totalBatches} complete. Progress: ${progressPercent}%`);
      
      // Add a small delay between batches to avoid overwhelming the server
      if (i + batchSize < filesToLoad.length) {
        await new Promise(resolve => setTimeout(resolve, batchDelay));
      }
    }

    // Clean up the main hidden audio ref if it was created
    if (hiddenAudioRef.current && document.body.contains(hiddenAudioRef.current)) {
      hiddenAudioRef.current.remove();
      hiddenAudioRef.current = null;
    }

    // Filter out any nulls from measuredDurations (these are failed loads)
    const validDurations = measuredDurations.filter(d => d !== null && d > 0) as number[];
    const failedCount = filesToLoad.length - validDurations.length;

    console.log(`Audio loading complete: ${validDurations.length} succeeded, ${failedCount} failed out of ${filesToLoad.length} loaded (${recordings.length} total)`);
    
    if (shouldLimitFiles) {
      console.warn(`⚠️ Only loaded ${filesToLoad.length} out of ${recordings.length} files. Consider refreshing the page after the combined file is generated.`);
    }

    // Log detailed error information
    if (errorDetails.length > 0) {
      console.error(`Failed to load ${errorDetails.length} out of ${recordings.length} audio files:`);
      errorDetails.forEach(({ index, key, url, error }) => {
        console.error(`  [${index + 1}] ${key}: ${error}`);
        console.error(`      URL: ${url.substring(0, 150)}...`);
      });
    }

    // 2. Calculate cumulative durations
    const cumulativeDurations: number[] = [];
    let currentCumulative = 0;
    for (const duration of validDurations) {
      currentCumulative += duration;
      cumulativeDurations.push(currentCumulative);
    }
    const totalDuration = currentCumulative;
    console.log(`Successfully loaded ${validDurations.length} audio files, failed: ${failedCount}`);
    console.log('Cumulative durations:', cumulativeDurations);

    // Check if we have any valid durations
    if (validDurations.length === 0) {
      console.error('No audio files could be loaded. This may be due to:');
      console.error('1. CORS issues - S3 bucket may not allow direct browser access');
      console.error('2. Authentication issues - URLs may need to be presigned');
      console.error('3. Network issues - Check browser console for detailed errors');
      setAudioLoadingState('complete'); // Still mark as complete to show error state
      setTotalAudioDuration(0);
      setAudioDurationMap({});
      return;
    }

    // 3. Build the duration map using cumulative durations
    const durationMap: { [startTime: number]: { fileIndex: number, offsetInFile: number } } = {};
    let unmappedCount = 0;
    transcriptParts.forEach(part => {
      let fileIndex = -1;
      let offsetInFile = -1;

      // Find the correct file index and offset
      for (let i = 0; i < cumulativeDurations.length; i++) {
        const previousCumulative = i === 0 ? 0 : cumulativeDurations[i - 1];
        const currentFileEnd = cumulativeDurations[i];

        if (part.start_time >= previousCumulative && part.start_time < currentFileEnd) {
          fileIndex = i;
          offsetInFile = part.start_time - previousCumulative;
          break; // Found the correct segment
        }
      }

      // Handle cases where start_time might exactly match the last cumulative duration (edge case)
      if (fileIndex === -1 && part.start_time === totalDuration && totalDuration > 0) {
        fileIndex = cumulativeDurations.length - 1; // Assign to the last file
        // Ensure measuredDurations index access is safe
        offsetInFile = validDurations[fileIndex] !== undefined ? validDurations[fileIndex] : 0;
      }

      if (fileIndex !== -1) {
        durationMap[part.start_time] = {
          fileIndex,
          offsetInFile
        };
      } else {
        unmappedCount++;
        // Only log first few warnings to avoid console spam
        if (unmappedCount <= 5) {
          console.warn(`Could not map start_time ${part.start_time} to any audio file. Total Duration: ${totalDuration}`);
        }
      }
    });

    if (unmappedCount > 5) {
      console.warn(`... and ${unmappedCount - 5} more unmapped transcript parts`);
    }

    console.log('Total audio duration:', totalDuration);
    console.log(`Duration map: ${Object.keys(durationMap).length} mapped, ${unmappedCount} unmapped`);

    // Update state with the measured durations and the new map
    setRecordings(updatedRecordings);
    setTotalAudioDuration(totalDuration);
    setAudioDurationMap(durationMap);

    setAudioLoadingState('complete');
    // Ensure progress shows 100% at the end
    setAudioLoadingProgress(100);
  };

  // Function to get audio duration using a specific audio element
  const getAudioDuration = (url: string, audio: HTMLAudioElement, suppressStalledLogs: boolean = false): Promise<number> => {
    return new Promise((resolve, reject) => {
      let resolved = false;
      let timer: NodeJS.Timeout | null = null;

      const cleanup = () => {
        if (timer) clearTimeout(timer);
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        audio.removeEventListener('error', onError);
        audio.removeEventListener('stalled', onStalled);
        audio.removeEventListener('abort', onAbort);
        // Temporary elements aren't added to DOM, so no need to removeChild
        // Just ensure references are released for GC
      };

      const onLoadedMetadata = () => {
        if (resolved) return;
        resolved = true;
        const duration = audio.duration;
        cleanup();
        // Check for invalid duration which can happen sometimes
        if (isNaN(duration) || !isFinite(duration)) {
          reject(new Error(`Invalid duration received: ${duration}`));
        } else {
          resolve(duration);
        }
      };

      const onStalled = () => {
        // Only log stalled warnings if not suppressed (to reduce console spam for large batches)
        // Stalled events are common when loading many files and don't always indicate failure
        if (!suppressStalledLogs) {
          console.warn(`Audio loading stalled for: ${url.substring(0, 100)}...`);
        }
      };

      const onAbort = () => {
        if (!resolved) {
          resolved = true;
          cleanup();
          reject(new Error(`Audio loading aborted`));
        }
      };

      const onError = (e: Event) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        // Try to get more specific error information if possible
        const error = audio.error;
        let errorMessage = 'Failed to load audio';
        if (error) {
          // Map error codes to human-readable messages
          const errorMessages: { [key: number]: string } = {
            1: 'MEDIA_ERR_ABORTED - Loading aborted',
            2: 'MEDIA_ERR_NETWORK - Network error (may be CORS or authentication issue)',
            3: 'MEDIA_ERR_DECODE - Decoding error',
            4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - Source not supported'
          };
          const errorCodeMessage = errorMessages[error.code] || `Error code ${error.code}`;
          errorMessage = `${errorCodeMessage}: ${error.message || 'Unknown error'}`;
        } else {
          errorMessage = 'Unknown error loading audio';
        }
        reject(new Error(errorMessage));
      };

      // Set a timeout to prevent hanging indefinitely
      // Reduced timeout for better responsiveness when loading many files
      const timeoutMs = 8000; // 8 second timeout (reduced from 15s)
      timer = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          reject(new Error(`Timeout after ${timeoutMs/1000}s - audio may be inaccessible (CORS/auth issue)`));
        }
      }, timeoutMs);

      audio.addEventListener('loadedmetadata', onLoadedMetadata);
      audio.addEventListener('error', onError);
      audio.addEventListener('stalled', onStalled);
      audio.addEventListener('abort', onAbort);
      audio.crossOrigin = 'anonymous'; // Try to handle CORS if possible
      audio.src = url;
      audio.preload = 'metadata'; // Important: only load metadata
      audio.load();
    });
  };

  // Cleanup effect for the main hidden audio ref if component unmounts during loading
  useEffect(() => {
    return () => {
      if (hiddenAudioRef.current && document.body.contains(hiddenAudioRef.current)) {
        hiddenAudioRef.current.remove();
        hiddenAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      // Guard: no shift id → nothing to fetch (also narrows shiftId to string
      // for the scoped endpoint calls below, which the raw SQL interpolation
      // previously side-stepped).
      if (!shiftId) {
        setIsLoading(false);
        return;
      }
      try {
        // feat-133 Batch-2: shift header + events come from shift-scoped endpoints
        // (authorized server-side via _authorize_shift → 404 on a foreign/absent
        // shift, closing the raw-transcript PII leak) instead of the unscoped
        // execute-select `WHERE shift.id = "${shiftId}"`. getShift returns the same
        // single-row header shape the Q1 SELECT produced. Typed `any` because the
        // component's ShiftDetails state governs the field shapes (as it did when
        // it consumed the raw execute-select row).
        const shiftDetail: any = await getShift(shiftId);

        // /events returns the full shift-event list (ORDER BY created); the
        // connection event (deviceInfo) is the first client_connected event —
        // replaces the separate Q2 connection-event query. One call serves both
        // the deviceInfo and the shift-events list below.
        const eventsRows = await getShiftEvents(shiftId);
        const connectionEvent = eventsRows.find((e) => e.event_type === 'client_connected');
        const deviceInfo = connectionEvent ? (connectionEvent.event_data as any) : null;

        setShiftDetails({
          serverName: shiftDetail.server_name,
          shiftCreated: shiftDetail.shift_created,
          shiftEnded: shiftDetail.shift_ended,
          promptTemplateVersionId: shiftDetail.prompt_template_version_id,
          role: shiftDetail.shift_role,
          serverDefaultRole: shiftDetail.server_default_role ?? null,
          effectiveRole: shiftDetail.effective_role ?? null,
          roleDisplayName: shiftDetail.role_display_name ?? null,
          promptTemplateName: shiftDetail.prompt_template_name ?? null,
          promptTemplateRole: shiftDetail.prompt_template_role ?? null,
          promptTemplateTenantId: shiftDetail.prompt_template_tenant_id ?? null,
          promptVersionNumber: shiftDetail.prompt_version_number ?? null,
          // MySQL TINYINT may arrive as 0/1 (or "0"/"1"); coerce at the edge.
          promptVersionIsActive:
            shiftDetail.prompt_version_is_active == null
              ? null
              : Boolean(Number(shiftDetail.prompt_version_is_active)),
          shiftAppVersion: shiftDetail.shift_app_version ?? null,
          shiftAppBuild: shiftDetail.shift_app_build ?? null,
          shiftPatchNumber: shiftDetail.shift_patch_number ?? null,
          shiftDeviceModel: shiftDetail.shift_device_model ?? null,
          deviceName: shiftDetail.device_name ?? null,
          deviceInfo: deviceInfo
        });

        // Fetch metric config for this shift's prompt version
        if (shiftDetail.prompt_template_version_id) {
          fetchVersionConfigs([shiftDetail.prompt_template_version_id]);
        }

        // Parse shift.summary_json — the endpoint returns parsed JSON, but a JSON
        // string is tolerated (same coercion the prior execute-select allowed).
        const rawSummary = shiftDetail.shift_summary_json;
        if (rawSummary) {
          try {
            const parsed = typeof rawSummary === 'string'
              ? JSON.parse(rawSummary)
              : rawSummary;
            setShiftSummary(parsed as ShiftSummaryJson);
          } catch (e) {
            console.warn('Failed to parse shift.summary_json', e);
            setShiftSummary(null);
          }
        } else {
          setShiftSummary(null);
        }

        // Fetch transcript parts (scoped; shift already authorized above).
        const transcriptRows = await getShiftTranscript(shiftId);
        console.log(`Fetched ${transcriptRows.length} transcript parts for shift ${shiftId}`);
        if (transcriptRows.length === 0) {
          console.warn(`No transcript parts found for shift ${shiftId}. This may indicate transcripts are still being processed.`);
        }
        setTranscriptParts(transcriptRows as any);

        // Shift events — already fetched above (eventsRows) for the connection event.
        // feat-295-on-device-connection-diagnostics: device_diag rows are engineering-only.
        setShiftEvents(visibleShiftEvents(eventsRows) as any);

        // Fetch interactions (scoped).
        const interactionRows = await getShiftInteractions(shiftId);
        setInteractions(interactionRows as any);

        // Fetch gem_transaction rows for this shift's interactions (non-fatal —
        // page renders without XP badges if the table doesn't exist yet).
        try {
          const gemRows = await getShiftGemTransactions(shiftId);
          const map: Record<string, { amount: number; description: string | null }> = {};
          for (const row of gemRows) {
            const interactionId = row.source_id as string;
            const amount = Number(row.amount) || 0;
            const description = (row.description as string | null) ?? null;
            if (map[interactionId]) {
              map[interactionId] = {
                amount: map[interactionId].amount + amount,
                description: [map[interactionId].description, description].filter(Boolean).join('; ') || null,
              };
            } else {
              map[interactionId] = { amount, description };
            }
          }
          setGemByInteractionId(map);
        } catch (gemErr) {
          console.warn('gem_transaction fetch failed (table may not exist yet):', gemErr);
        }

        // Fetch Revel POS orders linked to this shift (non-fatal)
        try {
          // Scoped Revel orders — the endpoint returns the FLAT rows (one per
          // item×link), preserving the revel_* source (DP2); groupOrderRows nests
          // them into order → items → links exactly as before.
          const revelRows = await getShiftPosOrders(shiftId);
          const grouped = groupOrderRows(revelRows as any);
          setRevelOrders(grouped);
          console.log(`Fetched ${grouped.length} Revel orders for shift ${shiftId}`);
        } catch (revelErr) {
          console.warn('Revel order fetch failed (tables may not exist yet):', revelErr);
        }

        // Fetch recordings (non-fatal — page still renders without audio)
        if (serverId) {
          try {
            console.log(`Fetching recordings for shift ${shiftId}, server ${serverId}`);
            const recordingsResponse = await apiCall(`/admin/shift/${shiftId}/recordings?server_id=${serverId}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            );

            if (!recordingsResponse.ok) {
              const errorText = await recordingsResponse.text();
              console.error(`Failed to fetch recordings: ${recordingsResponse.status} ${errorText}`);
            } else {
              const recordingsData = await recordingsResponse.json();
              console.log(`Fetched ${recordingsData.recordings?.length || 0} recordings:`,
                recordingsData.recordings?.map((r: Recording) => r.key) || []);

              // Convert relative proxy URLs to absolute URLs
              const recordingsWithAbsoluteUrls = (recordingsData.recordings || []).map((recording: Recording) => {
                if (recording.url && recording.url.startsWith('/')) {
                  // Convert relative URL to absolute URL using the backend host
                  const protocol = RESTAURANT_BACKEND_HOST.includes('localhost') ? 'http' : 'https';
                  recording.url = `${protocol}://${RESTAURANT_BACKEND_HOST}${recording.url}`;
                }
                // Store the file size for potential duration calculation fallback
                if (recording.size) {
                  (recording as any).fileSize = recording.size;
                }
                return recording;
              });

              setRecordings(recordingsWithAbsoluteUrls);
            }
          } catch (recordingsErr) {
            console.error('Error fetching recordings:', recordingsErr);
          }
        } else {
          console.warn('No serverId provided, skipping recordings fetch');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [shiftId, serverId]);

  // Effect to start loading audio files after initial data load
  // Only load audio if transcripts exist (no point loading audio without transcripts to sync with)
  useEffect(() => {
    // Skip audio loading if transcripts don't exist yet
    if (transcriptParts.length === 0) {
      console.log('Skipping audio loading - no transcript parts available yet');
      return;
    }

    if (!isLoading && recordings.length > 0 && audioLoadingState === 'idle') {
      console.log(`Audio loading effect triggered: ${recordings.length} recordings, isCombinedFile: ${isCombinedFile}`);
      // For combined file, we don't need to load durations - the audio element will handle it
      if (isCombinedFile) {
        console.log('Using combined file - waiting for metadata to load');
        console.log(`Audio URL: ${recordings[0]?.url?.substring(0, 100)}...`);
        // Reset the loaded flag
        combinedFileLoadedRef.current = false;
        // Just mark as loading, duration will be set by onLoadedMetadata
        setAudioLoadingState('loading');
        setAudioLoadingProgress(10); // Start with 10% to show activity
        
        // Set a timeout to detect if metadata loading is stuck
        const timeout = setTimeout(() => {
          if (!combinedFileLoadedRef.current) {
            console.error('Timeout waiting for combined file metadata to load (20s)');
            console.error('This may indicate a CORS issue, authentication problem, or network issue');
            console.error('Audio URL:', recordings[0]?.url);
            // Try one last time to get duration from the audio element
            if (audioRef.current) {
              const duration = audioRef.current.duration;
              if (!isNaN(duration) && isFinite(duration) && duration > 0) {
                console.log(`Got duration on timeout: ${duration}s`);
                combinedFileLoadedRef.current = true;
                setTotalAudioDuration(duration);
                setAudioLoadingState('complete');
                setAudioLoadingProgress(100);
                return;
              }
            }
            setAudioLoadingState('complete');
            setTotalAudioDuration(0);
            setAudioLoadingProgress(100);
          }
        }, 20000); // 20 second timeout
        
        return () => clearTimeout(timeout);
      } else {
        console.log('Loading durations for chunked files...');
        loadAudioDurations();
      }
    } else if (!isLoading && recordings.length === 0) {
      console.log('No recordings found for this shift');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, recordings.length, audioLoadingState, isCombinedFile, transcriptParts.length]);

  // Combine and sort transcript parts, shift events, and revel orders by timestamp
  useEffect(() => {
    // Compute a base time from shiftCreated so transcripts and orders use the same time basis
    const shiftCreatedUtc = shiftDetails?.shiftCreated
      ? (shiftDetails.shiftCreated.endsWith('Z')
          ? shiftDetails.shiftCreated
          : `${shiftDetails.shiftCreated}Z`)
      : null;
    const baseTime = shiftCreatedUtc ? new Date(shiftCreatedUtc).getTime() : null;

    // Create timeline items from transcript parts
    // Use shiftCreated + start_time so they sort on the same basis as orders
    const transcriptItems: TimelineItem[] = transcriptParts.map(part => ({
      type: 'transcript',
      data: part,
      created: baseTime
        ? new Date(baseTime + part.start_time * 1000).toISOString()
        : part.created,
    }));

    // Create timeline items from shift events
    const eventItems: TimelineItem[] = shiftEvents.map(event => ({
      type: 'event',
      data: event,
      created: event.created
    }));

    // Create timeline items from revel orders using the same base
    const orderItems: TimelineItem[] = revelOrders.map(order => ({
      type: 'order' as const,
      data: order,
      created: baseTime
        ? new Date(baseTime + order.shift_offset_seconds * 1000).toISOString()
        : new Date().toISOString(),
    }));

    // Combine and sort
    const combined = [...transcriptItems, ...eventItems, ...orderItems].sort((a, b) => {
      return new Date(a.created).getTime() - new Date(b.created).getTime();
    });

    setTimelineItems(combined);
  }, [transcriptParts, shiftEvents, revelOrders, shiftDetails]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnded = () => {
    // For combined file, just stop playing
    if (isCombinedFile) {
      setIsPlaying(false);
      return;
    }
    
    // For chunked files, move to next chunk
    if (currentRecordingIndex < recordings.length - 1) {
      setCurrentRecordingIndex(currentRecordingIndex + 1);
      // The audio will automatically play the next recording due to the useEffect below
    } else {
      setIsPlaying(false);
    }
  };

  // Force audio to load metadata for combined files
  useEffect(() => {
    if (isCombinedFile && recordings.length > 0 && audioLoadingState === 'loading') {
      console.log('🔍 Audio loading useEffect triggered');
      console.log('  isCombinedFile:', isCombinedFile);
      console.log('  recordings.length:', recordings.length);
      console.log('  audioLoadingState:', audioLoadingState);
      console.log('  audioRef.current:', audioRef.current ? 'exists' : 'null');
      
      // Use a small delay to ensure the audio element is mounted in the DOM
      const initAudio = setTimeout(() => {
        if (!audioRef.current) {
          console.warn('⚠️ Audio ref not available yet, will retry...');
          // Retry after another delay
          const retryTimeout = setTimeout(() => {
            if (audioRef.current && audioLoadingState === 'loading') {
              console.log('🔄 Retrying audio initialization...');
              const audio = audioRef.current;
              audio.src = recordings[0].url;
              audio.load();
            }
          }, 500);
          return () => clearTimeout(retryTimeout);
        }
        
        const audio = audioRef.current;
        console.log('✅ Forcing audio metadata load for combined file');
        console.log('  Audio URL:', recordings[0].url);
        console.log('  Current audio src:', audio.src);
        console.log('  Audio readyState:', audio.readyState);
        console.log('  Audio networkState:', audio.networkState);
        
        // Ensure the src is set
        if (audio.src !== recordings[0].url) {
          console.log('  Setting audio src to:', recordings[0].url);
          audio.src = recordings[0].url;
        }
        
        // Force load
        console.log('  Calling audio.load()...');
        try {
          audio.load();
          console.log('  audio.load() called successfully');
        } catch (error) {
          console.error('  Error calling audio.load():', error);
        }
        
        // Also set up a polling mechanism to check if duration becomes available
        // This is a fallback in case events don't fire
        let pollCount = 0;
        const checkDuration = setInterval(() => {
          pollCount++;
          const duration = audio.duration;
          const readyState = audio.readyState;
          const networkState = audio.networkState;
          
          if (pollCount <= 5 || pollCount % 10 === 0) {
            // Log first 5 polls and then every 10th poll
            console.log(`📊 Poll ${pollCount}: duration=${duration}, readyState=${readyState}, networkState=${networkState}, buffered=${audio.buffered.length > 0 ? audio.buffered.end(0) : 'none'}`);
          }
          
          if (!isNaN(duration) && isFinite(duration) && duration > 0 && !combinedFileLoadedRef.current) {
            console.log(`✅ Duration detected via polling: ${duration}s`);
            combinedFileLoadedRef.current = true;
            setTotalAudioDuration(duration);
            setAudioLoadingState('complete');
            setAudioLoadingProgress(100);
            clearInterval(checkDuration);
          }
          
          // Stop polling after 40 checks (20 seconds)
          if (pollCount >= 40) {
            clearInterval(checkDuration);
            if (!combinedFileLoadedRef.current) {
              console.warn('⚠️ Duration check timeout after 20s - metadata may not be available');
              console.warn('  Final state - duration:', duration, 'readyState:', readyState, 'networkState:', networkState);
              // Even if we didn't get duration, mark as complete so UI doesn't hang
              setAudioLoadingState('complete');
              setAudioLoadingProgress(100);
            }
          }
        }, 500); // Check every 500ms
        
        return () => {
          clearInterval(checkDuration);
        };
      }, 100); // Small delay to ensure DOM is ready
      
      return () => clearTimeout(initAudio);
    }
  }, [isCombinedFile, recordings, audioLoadingState]);

  // Update current time for combined file slider
  useEffect(() => {
    if (!isCombinedFile || !audioRef.current) return;

    const audio = audioRef.current;
    const updateTime = () => setCurrentTime(audio.currentTime);
    
    audio.addEventListener('timeupdate', updateTime);
    return () => audio.removeEventListener('timeupdate', updateTime);
  }, [isCombinedFile]);

  useEffect(() => {
    if (audioRef.current && recordings.length > 0 && isPlaying && !isCombinedFile) {
      audioRef.current.play();
    }
  }, [currentRecordingIndex, recordings.length, isCombinedFile, isPlaying]);

  const handlePlayFromTimestamp = (startTime: number) => {
    if (audioRef.current && recordings.length > 0 && audioLoadingState === 'complete') {
      // For combined file, just set the time directly
      if (isCombinedFile) {
        audioRef.current.currentTime = startTime;
        audioRef.current.play();
        setIsPlaying(true);
        return;
      }

      // For chunked files, get the correct file and position from our duration map
      const fileInfo = audioDurationMap[startTime];

      if (fileInfo) {
        // Set the current recording index to the correct file
        setCurrentRecordingIndex(fileInfo.fileIndex);

        // We need to wait for the audio src to update (in the next render cycle)
        setTimeout(() => {
          if (audioRef.current) {
            // Set the current time to the correct offset in the file
            audioRef.current.currentTime = fileInfo.offsetInFile;
            // Start playing
            audioRef.current.play();
            setIsPlaying(true);
          }
        }, 50);
      } else {
        console.warn(`No file info found for timestamp ${startTime}`);
      }
    }
  };

  const handleSeek = (newTime: number) => {
    if (audioRef.current && isCombinedFile) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleMarkInteraction = async (interactionId: string, status: boolean | null) => {
    try {
      const response = await apiCall(`/shift-interactions/${interactionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ correct_human_verified: status })
        }
      );

      if (!response.ok) {
        // feat-131: the grade endpoint is now admin-authenticated. Surface the
        // authorization failure explicitly instead of a generic "failed" so an
        // unauthorized/expired user isn't left guessing.
        if (response.status === 401) {
          alert('Your session has expired. Please sign in again to grade interactions.');
          return;
        }
        if (response.status === 403) {
          alert('You are not authorized to grade interactions. Only Bespoke admins can verify interaction correctness.');
          return;
        }
        throw new Error('Failed to update interaction');
      }

      // Update the interaction in local state
      setInteractions(interactions.map(i =>
        i.id === interactionId
          ? { ...i, correct_human_verified: status }
          : i
      ));
    } catch (err) {
      console.error('Error updating interaction:', err);
      alert('Failed to update interaction');
    }
  };

  const handleReportBug = async () => {
    if (isReportingBug) return;
    setIsReportingBug(true);
    try {
      const stats = calculateCriteriaStats().filter(s => s.total > 0);
      const criteria_stats = stats.length > 0
        ? stats.map(s => ({
            label: criteriaLabels[s.criterionType] || s.criterionType,
            fulfilled: s.fulfilled,
            total: s.total,
            percentage: s.percentage,
          }))
        : undefined;

      const interactionSummaries = interactions.length > 0
        ? interactions.map(i => ({
            interaction_type: i.interaction_type,
            start_time: i.start_time,
            verified: i.correct_human_verified,
          }))
        : undefined;

      const shift_date_label = shiftDetails?.shiftCreated
        ? (() => {
            const utc = shiftDetails.shiftCreated.endsWith('Z') ? shiftDetails.shiftCreated : `${shiftDetails.shiftCreated}Z`;
            return formatInTimeZone(parseISO(utc), 'America/Los_Angeles', 'yyyy.MM.dd');
          })()
        : undefined;

      const response = await apiCall('/api/admin/notion/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: window.location.href,
          shift_id: shiftId,
          server_name: shiftDetails?.serverName,
          shift_started: shiftDetails?.shiftCreated,
          shift_date_label,
          role: shiftDetails?.role,
          prompt_template_version_id: shiftDetails?.promptTemplateVersionId,
          criteria_stats,
          interactions: interactionSummaries,
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Notion request failed (${response.status})`);
      }
      const data = await response.json();
      if (data.page_url) {
        window.open(data.page_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Bug created in Notion, but no page URL was returned.');
      }
    } catch (err) {
      console.error('Failed to create Notion bug entry:', err);
      alert(`Failed to create Notion bug entry: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setIsReportingBug(false);
    }
  };

  const handleCreateTestCase = async () => {
    if (!shiftDetails?.promptTemplateVersionId || transcriptParts.length === 0) return;

    const formattedDate = shiftDetails.shiftCreated
      ? (() => {
          const utc = shiftDetails.shiftCreated.endsWith('Z') ? shiftDetails.shiftCreated : `${shiftDetails.shiftCreated}Z`;
          return formatInTimeZone(parseISO(utc), 'America/Los_Angeles', 'yyyy.MM.dd h:mm a');
        })()
      : 'Unknown date';
    const defaultName = `${shiftDetails.serverName} – ${formattedDate}`;
    const name = window.prompt('Test case name:', defaultName);
    if (!name) return;

    setIsCreatingTestCase(true);
    try {
      const version = await getVersion(shiftDetails.promptTemplateVersionId);
      const template = await getTemplate(version.template_id);

      const transcript_json = transcriptParts.map((part) => ({
        speaker: part.speaker,
        text: part.text,
        timestamp: part.start_time,
      }));

      const annotated_interactions = interactions
        .filter((i) => i.interaction_type !== 'drink_promotion')
        .map((i) => ({
          interaction_type: i.interaction_type,
          start_timestamp: i.start_time,
          end_timestamp: i.end_time,
          service_criteria: (i.data?.service_criteria || []).map(
            (sc: { type: string; fulfilled: boolean }) => ({ type: sc.type, fulfilled: sc.fulfilled })
          ),
        }));

      const drink_promotions = interactions
        .filter((i) => i.interaction_type === 'drink_promotion')
        .map((i) => ({
          start_timestamp: i.start_time,
          end_timestamp: i.end_time,
          drink_promoted: i.data?.drink_promoted || '',
        }));

      const expected_interactions = { annotated_interactions, drink_promotions };
      const source_url = `/restaurant/shifts/${shiftId}/transcript/${serverId}`;

      await createTestCase(version.template_id, {
        name,
        transcript_json,
        expected_interactions,
        source_url,
      });

      navigate(`/prompt-management?tenantId=${template.tenant_id}`);
    } catch (err: any) {
      console.error('Error creating test case:', err);
      alert(`Failed to create test case: ${err.message || err}`);
    } finally {
      setIsCreatingTestCase(false);
    }
  };

  const handleDownloadFullWav = async (recording: Recording) => {
    if (!recording?.url) return;
    setIsDownloadingFullWav(true);
    try {
      // Fetch as a blob so we can force the filename and avoid the
      // "open in new tab" behavior that S3-presigned URLs trigger when
      // an <a download> attribute is used cross-origin.
      const response = await fetch(recording.url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Filename: shift-<shortId>-full.wav so multiple downloads stay distinct
      const shortShift = (shiftId || 'unknown').slice(0, 8);
      const filename = `shift-${shortShift}-full.wav`;

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in a new tab so the user can save it manually
      window.open(recording.url, '_blank', 'noopener,noreferrer');
      alert(
        'Direct download failed (probably CORS on the S3 URL). Opened the file in a new tab — use your browser\'s save dialog from there.',
      );
    } finally {
      setIsDownloadingFullWav(false);
    }
  };

  const handleCombineAudio = async () => {
    if (!shiftId || !serverId) {
      alert('Missing shift ID or server ID');
      return;
    }

    setIsCombiningAudio(true);
    setCombineAudioStatus('Starting audio combination...');

    try {
      const response = await apiCall(
        `/admin/shift/${shiftId}/combine-audio?server_id=${serverId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to start audio combination: ${errorText}`);
      }

      const data = await response.json();
      setCombineAudioStatus(data.message || 'Audio combination started. This may take several minutes...');

      // Poll for the combined file to appear
      let attempts = 0;
      const maxAttempts = 60; // Check for up to 5 minutes (5 second intervals)
      const pollInterval = setInterval(async () => {
        attempts++;
        
        try {
          const recordingsResponse = await apiCall(
            `/admin/shift/${shiftId}/recordings?server_id=${serverId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (recordingsResponse.ok) {
            const recordingsData = await recordingsResponse.json();
            const hasCombinedFile = recordingsData.recordings?.some((r: Recording) => 
              r.key?.includes('full.')
            );

            if (hasCombinedFile) {
              clearInterval(pollInterval);
              setIsCombiningAudio(false);
              setCombineAudioStatus('Audio combination complete! Refreshing...');
              
              // Refresh the page data
              window.location.reload();
            } else if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              setIsCombiningAudio(false);
              setCombineAudioStatus('Combination is taking longer than expected. Please refresh the page in a few minutes.');
            } else {
              setCombineAudioStatus(`Combining audio... (checking ${attempts}/${maxAttempts})`);
            }
          }
        } catch (err) {
          console.error('Error checking for combined file:', err);
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setIsCombiningAudio(false);
            setCombineAudioStatus('Error checking status. Please refresh the page in a few minutes.');
          }
        }
      }, 5000); // Check every 5 seconds

    } catch (err) {
      console.error('Error combining audio:', err);
      setIsCombiningAudio(false);
      setCombineAudioStatus(null);
      alert(`Failed to start audio combination: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Filter interactions based on verification status and type (copied from ShiftPerformanceView)
  const getInitialGreetingInteractions = () => {
    return interactions.filter(interaction => {
      // Filter by verification status if needed
      if (showVerifiedOnly && interaction.correct_human_verified !== true) {
        return false;
      }

      // Include both old format (initial_greeting) and new format (greeter_upsell, cashier_upsell) interactions
      if (interaction.interaction_type !== 'greeter_greeting' && 
          interaction.interaction_type !== 'greeter_upsell' && 
          interaction.interaction_type !== 'cashier_upsell') {
        return false;
      }

      // Check if the interaction has service_criteria data
      return interaction.data && interaction.data.service_criteria;
    });
  };

  const getDrinkPromotionInteractions = () => {
    return interactions.filter(interaction => {
      // Filter by verification status if needed
      if (showVerifiedOnly && interaction.correct_human_verified !== true) {
        return false;
      }

      // Only include drink_promotion interactions
      if (interaction.interaction_type !== 'drink_promotion') {
        return false;
      }

      // Check if the interaction has service_criteria data
      return true
    });
  };

  // Calculate statistics for each criterion (copied from ShiftPerformanceView)
  const calculateCriteriaStats = (): CriteriaStats[] => {
    const filteredInteractions = getInitialGreetingInteractions();
    const drinkPromotionInteractions = getDrinkPromotionInteractions();
    const totalInitialGreetings = filteredInteractions.length;

    const stats = criteriaTypes.map(criterionType => {
      let fulfilled = 0;
      let total = 0;

      filteredInteractions.forEach(interaction => {
        const serviceCriteria = interaction.data.service_criteria as ServiceCriterion[];
        if (!serviceCriteria) return;

        const criterion = serviceCriteria.find(c => c.type === criterionType);
        if (criterion) {
          total++;
          if (criterion.fulfilled) {
            fulfilled++;
          }
        }
      });

      const percentage = total > 0 ? (fulfilled / total) * 100 : 0;

      return {
        criterionType,
        fulfilled,
        total,
        percentage
      };
    });

    // Add drink promotion stat
    const drinkPromotionPercentage = totalInitialGreetings > 0
      ? (drinkPromotionInteractions.length / totalInitialGreetings) * 100
      : 0;

    stats.push({
      criterionType: "drink_promotion",
      fulfilled: drinkPromotionInteractions.length,
      total: totalInitialGreetings,
      percentage: drinkPromotionPercentage
    });

    return stats;
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';

    try {
      // The date string from the database is now in UTC (after CONVERT_TZ in SQL)
      // Ensure it's treated as UTC by appending 'Z' if not present
      const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
      const date = parseISO(utcDateString);
      
      // Format the date in Pacific time (America/Los_Angeles handles both PST and PDT automatically)
      // This will correctly account for daylight savings time
      return formatInTimeZone(date, 'America/Los_Angeles', 'MMMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  // Calculate duration in hours and minutes
  const calculateDuration = (startDateString: string | null, endDateString: string | null) => {
    if (!startDateString || !endDateString) return 'N/A';

    // The date strings from the database are now in UTC (after CONVERT_TZ in SQL)
    // Ensure they're treated as UTC by appending 'Z' if not present
    const startUtcString = startDateString.endsWith('Z') ? startDateString : `${startDateString}Z`;
    const endUtcString = endDateString.endsWith('Z') ? endDateString : `${endDateString}Z`;

    // Create date objects from UTC strings
    const startDate = new Date(startUtcString);
    const endDate = new Date(endUtcString);

    // Calculate difference in milliseconds (works correctly with UTC timestamps)
    const diffMs = endDate.getTime() - startDate.getTime();

    // Convert to hours and minutes
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  // Format shift event data for display
  const formatEventData = (data: any): string => {
    try {
      if (typeof data === 'string') {
        // Try to parse if it's a JSON string
        return JSON.stringify(JSON.parse(data), null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return typeof data === 'string' ? data : JSON.stringify(data);
    }
  };

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (error) {
    return <div className="text-red-500 p-4">Error: {error}</div>;
  }

  // Show a more helpful message if recordings exist but transcript parts don't
  if (transcriptParts.length === 0) {
    if (recordings.length > 0) {
      return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-2xl font-bold">Transcript for Shift #{shiftId}</h1>
          </div>

          {shiftDetails && (
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500">Server</h3>
                  <Link
                    to={`/restaurant/servers/performance?serverId=${serverId}&advanced=true`}
                    className="text-lg font-medium text-blue-600 hover:text-blue-800"
                  >
                    {shiftDetails.serverName}
                  </Link>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-500">Started</h3>
                  <p className="text-lg">{formatDate(shiftDetails.shiftCreated)}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-500">Duration</h3>
                  <p className="text-lg">{calculateDuration(shiftDetails.shiftCreated, shiftDetails.shiftEnded)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-yellow-800 mb-2">Transcript Processing</h2>
            <p className="text-yellow-700 mb-2">
              Audio recordings are available ({recordings.length} files), but transcript data is still being processed.
            </p>
            <div className="mt-4 space-y-2">
              <p className="text-sm text-yellow-600">
                <strong>What this means:</strong> The audio files have been recorded and uploaded, but the transcription service hasn't finished processing them yet.
              </p>
              <p className="text-sm text-yellow-600">
                <strong>What to do:</strong> Please check back in a few minutes. The transcript will appear here once processing is complete.
              </p>
              {recordings.length > 10 && (
                <p className="text-xs text-yellow-600 mt-2 italic">
                  Note: This shift has {recordings.length} audio files, which may take longer to process.
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold">Transcript for Shift #{shiftId}</h1>
        </div>
        {shiftDetails && (
          <div className="bg-white shadow-md rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-500">Server</h3>
                <Link
                  to={`/restaurant/servers/performance?serverId=${serverId}&advanced=true`}
                  className="text-lg font-medium text-blue-600 hover:text-blue-800"
                >
                  {shiftDetails.serverName}
                </Link>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500">Started</h3>
                <p className="text-lg">{formatDate(shiftDetails.shiftCreated)}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500">Duration</h3>
                <p className="text-lg">{calculateDuration(shiftDetails.shiftCreated, shiftDetails.shiftEnded)}</p>
              </div>
            </div>
          </div>
        )}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">No Data Available</h2>
          <p className="text-red-700 mb-2">
            No transcript data or audio recordings are available for this shift.
          </p>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-red-600">
              <strong>Possible reasons:</strong>
            </p>
            <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
              <li>The connection was established but no audio data was received</li>
              <li>The recording was interrupted before any data could be saved</li>
              <li>The shift may reconnect and continue recording (check back later)</li>
            </ul>
            <p className="text-xs text-red-500 mt-2 italic">
              Note: If you see a "client_disconnected_no_audio" event in the shift events, this indicates the connection closed without receiving audio data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" ref={transcriptRef}>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold">Transcript for Shift #{shiftId}</h1>
        <div className="flex gap-2">
          <button
            onClick={handleReportBug}
            disabled={isReportingBug}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
              isReportingBug
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-300'
            }`}
            title="Open a new entry in the Notion QA Bug Board with this transcript URL pre-filled"
          >
            {isReportingBug ? 'Creating...' : 'Report a Bug'}
          </button>
          <button
            onClick={handleCreateTestCase}
            disabled={!shiftDetails?.promptTemplateVersionId || transcriptParts.length === 0 || isCreatingTestCase}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
              shiftDetails?.promptTemplateVersionId && transcriptParts.length > 0 && !isCreatingTestCase
                ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 border border-purple-300'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
            }`}
            title={
              !shiftDetails?.promptTemplateVersionId
                ? 'No prompt template associated with this shift'
                : transcriptParts.length === 0
                  ? 'No transcript data available'
                  : 'Create a prompt management test case from this shift'
            }
          >
            {isCreatingTestCase ? 'Creating...' : 'Create Test Case'}
          </button>
        </div>
      </div>

      {shiftDetails && (
        <div className="bg-white shadow-md rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-500">Server</h3>
              <Link
                to={`/restaurant/servers/performance?serverId=${serverId}&advanced=true`}
                className="text-lg font-medium text-blue-600 hover:text-blue-800"
              >
                {shiftDetails.serverName}
              </Link>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-500">Started</h3>
              <p className="text-lg">{formatDate(shiftDetails.shiftCreated)}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-500">Duration</h3>
              <p className="text-lg">{calculateDuration(shiftDetails.shiftCreated, shiftDetails.shiftEnded)}</p>
            </div>
            {/* feat-319: Role | Prompt — two more cells in the same grid; at md
                they wrap onto row 2 under Server/Started (design/NOTES.md §1). */}
            <ShiftRolePromptRow
              shiftRole={shiftDetails.role}
              effectiveRole={shiftDetails.effectiveRole}
              roleDisplayName={shiftDetails.roleDisplayName}
              promptTemplateVersionId={shiftDetails.promptTemplateVersionId}
              promptTemplateName={shiftDetails.promptTemplateName}
              promptTemplateRole={shiftDetails.promptTemplateRole}
              promptTemplateTenantId={shiftDetails.promptTemplateTenantId}
              promptVersionNumber={shiftDetails.promptVersionNumber}
              promptVersionIsActive={shiftDetails.promptVersionIsActive}
            />
          </div>

          {/* App version snapshot — frozen at shift creation, accurate
              regardless of any device upgrade since this shift ran. */}
          {(shiftDetails.shiftAppVersion || shiftDetails.shiftDeviceModel || shiftDetails.deviceName) && (
            <p className="mt-3 text-xs text-gray-500">
              Recorded by{' '}
              {shiftDetails.deviceName ? (
                <span className="font-medium text-gray-700">{shiftDetails.deviceName}</span>
              ) : (
                <span className="italic">unnamed device</span>
              )}
              {shiftDetails.shiftAppVersion ? (
                <>
                  {' with app v'}{shiftDetails.shiftAppVersion}
                  {shiftDetails.shiftAppBuild && <> (build {shiftDetails.shiftAppBuild})</>}
                  {shiftDetails.shiftPatchNumber != null && <>{', patch '}{shiftDetails.shiftPatchNumber}</>}
                </>
              ) : (
                ' with app version unknown'
              )}
              {shiftDetails.shiftDeviceModel && <> on {shiftDetails.shiftDeviceModel}</>}
              .
            </p>
          )}

          {shiftDetails.deviceInfo && (
            <div className="mt-4 border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-500 mb-2">Client Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500">Client Type</p>
                  <p className="text-sm">{shiftDetails.deviceInfo.client_type}</p>
                </div>
                {shiftDetails.deviceInfo.device_name && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">Device Name</p>
                    <p className="text-sm">{shiftDetails.deviceInfo.device_name}</p>
                  </div>
                )}
                {shiftDetails.deviceInfo.device_model && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">Device Model</p>
                    <p className="text-sm">{shiftDetails.deviceInfo.device_model}</p>
                  </div>
                )}
                {shiftDetails.deviceInfo.system_name && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">OS</p>
                    <p className="text-sm">{shiftDetails.deviceInfo.system_name} {shiftDetails.deviceInfo.system_version}</p>
                  </div>
                )}
                {shiftDetails.deviceInfo.app_version && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">App Version</p>
                    <p className="text-sm">{shiftDetails.deviceInfo.app_version}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collapsible: Performance Summary + Interactions + Audio Recordings */}
      <div className="bg-white shadow-md rounded-lg mb-6 overflow-hidden">
        <button
          onClick={() => setShowPerformanceDetails(!showPerformanceDetails)}
          className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors text-left"
          aria-expanded={showPerformanceDetails}
        >
          <h2 className="text-lg font-semibold">Performance details</h2>
          <span className="flex items-center gap-2 text-gray-500 text-sm">
            {showPerformanceDetails ? 'Hide' : 'Show'}
            {showPerformanceDetails ? <FaChevronDown /> : <FaChevronRight />}
          </span>
        </button>
        {showPerformanceDetails && (
          <div className="px-4 pb-4 space-y-6 border-t border-gray-100 pt-4">
      {/* Add performance summary section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Performance Summary</h2>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-blue-600 mr-2"
                checked={showVerifiedOnly}
                onChange={() => setShowVerifiedOnly(!showVerifiedOnly)}
                id="verified-checkbox"
              />
              <label htmlFor="verified-checkbox" className="text-gray-700 text-sm">
                Show only human-verified interactions
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-blue-600 mr-2"
                checked={showAllInteractionTypes}
                onChange={() => setShowAllInteractionTypes(!showAllInteractionTypes)}
                id="show-all-types-checkbox"
              />
              <label htmlFor="show-all-types-checkbox" className="text-gray-700 text-sm">
                Show all interaction types
              </label>
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-4">
          {calculateCriteriaStats().map(stat => (
            <div key={stat.criterionType} className="flex flex-col">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">{criteriaLabels[stat.criterionType]}</span>
                <span className="text-sm font-medium">{stat.fulfilled}/{stat.total} ({stat.percentage.toFixed(1)}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${stat.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {interactions.filter(isInteractionTypeVisible).length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Interactions</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left">Time</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {interactions.filter(isInteractionTypeVisible).map((interaction) => (
                  <tr key={interaction.id} className="border-t">
                    <td className="px-4 py-2">
                      {formatTimestamp(interaction.start_time)} - {formatTimestamp(interaction.end_time)}
                    </td>
                    <td className="px-4 py-2">
                      {(interaction.end_time - interaction.start_time).toFixed(1)}s
                    </td>
                    <td className="px-4 py-2 capitalize">{interaction.interaction_type}</td>
                    <td className="px-4 py-2">
                      {interaction.correct_human_verified !== null && (
                        <span className={`text-xs font-medium ${interaction.correct_human_verified
                          ? 'text-green-600'
                          : 'text-red-600'
                          }`}>
                          {interaction.correct_human_verified
                            ? 'Verified Correct'
                            : 'Verified Incorrect'}
                        </span>
                      )}
                      {interaction.correct_human_verified === null && (
                        <span className="text-xs text-gray-500">
                          Not Verified
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => {
                          const element = document.querySelector(`[data-time="${interaction.start_time}"]`);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                          handlePlayFromTimestamp(interaction.start_time);
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        Jump to section
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {recordings.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Audio Recordings</h2>
            <div className="flex gap-2">
              {isCombinedFile && (
                <button
                  onClick={() => handleDownloadFullWav(recordings[0])}
                  disabled={isDownloadingFullWav}
                  className={`px-4 py-2 rounded font-medium ${
                    isDownloadingFullWav
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  title="Download full.wav — useful as a ground-truth fixture"
                >
                  {isDownloadingFullWav ? 'Downloading…' : 'Download full.wav'}
                </button>
              )}
              {hasChunksButNoCombined && (
                <button
                  onClick={handleCombineAudio}
                  disabled={isCombiningAudio}
                  className={`px-4 py-2 rounded font-medium ${
                    isCombiningAudio
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                  title="Combine all audio chunks into one seamless recording"
                >
                  {isCombiningAudio ? 'Combining...' : 'Combine Audio'}
                </button>
              )}
            </div>
          </div>
          {combineAudioStatus && (
            <div className={`mb-4 p-3 rounded ${
              isCombiningAudio ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800'
            }`}>
              <p className="text-sm">{combineAudioStatus}</p>
            </div>
          )}

          {/* Always render audio element for combined files, even during loading */}
          {isCombinedFile && recordings.length > 0 && (
            <audio
              ref={audioRef}
              src={recordings[0]?.url}
              onEnded={handleAudioEnded}
              onLoadedMetadata={() => {
                console.log('🎵 Combined file metadata loaded');
                if (audioRef.current) {
                  let duration = audioRef.current.duration;
                  console.log(`🎵 Combined file duration from metadata: ${duration}s`);
                  
                  // If duration is Infinity, try to get it from the file size
                  // For WAV files, we can estimate: duration ≈ (file_size - 44) / (sample_rate * channels * bits_per_sample / 8)
                  // But we don't have that info, so we'll try to wait for more data or use a fallback
                  if (!isFinite(duration) || duration === Infinity) {
                    console.warn('⚠️ Duration is Infinity, will try to get from file size or wait for more data');
                    // Don't mark as loaded yet - let other events or polling handle it
                    return;
                  }
                  
                  if (isNaN(duration) || duration <= 0) {
                    console.error('❌ Invalid duration received:', duration);
                    setAudioLoadingState('complete');
                    setTotalAudioDuration(0);
                    setAudioLoadingProgress(100);
                  } else {
                    combinedFileLoadedRef.current = true;
                    setTotalAudioDuration(duration);
                    setAudioLoadingState('complete');
                    setAudioLoadingProgress(100);
                  }
                }
              }}
                onCanPlay={() => {
                  console.log('🎵 Combined file can play - metadata should be available');
                  if (audioRef.current && !combinedFileLoadedRef.current) {
                    // Fallback: if onLoadedMetadata didn't fire, try to get duration from onCanPlay
                    let duration = audioRef.current.duration;
                    console.log(`🎵 Duration from onCanPlay: ${duration}s`);
                    
                    // If duration is still Infinity, try to estimate from file size
                    if (!isFinite(duration) || duration === Infinity) {
                      const recording = recordings[0];
                      if (recording && (recording as any).fileSize) {
                        // For WAV files: rough estimate assuming 16-bit, 44.1kHz, mono
                        // WAV header is ~44 bytes, so audio data = file_size - 44
                        // duration ≈ (file_size - 44) / (sample_rate * channels * bits_per_sample / 8)
                        // Using conservative estimate: 16-bit, 44.1kHz, mono = 88200 bytes/sec
                        const fileSize = (recording as any).fileSize;
                        const estimatedDuration = (fileSize - 44) / 88200;
                        if (estimatedDuration > 0 && estimatedDuration < 3600) { // Reasonable range: 0 to 1 hour
                          console.log(`🎵 Estimated duration from file size: ${estimatedDuration.toFixed(2)}s (file size: ${fileSize} bytes)`);
                          duration = estimatedDuration;
                        }
                      }
                    }
                    
                    if (!isNaN(duration) && isFinite(duration) && duration > 0) {
                      console.log(`🎵 Using duration: ${duration}s`);
                      combinedFileLoadedRef.current = true;
                      setTotalAudioDuration(duration);
                      setAudioLoadingState('complete');
                      setAudioLoadingProgress(100);
                    }
                  }
                }}
              onLoadedData={() => {
                console.log('🎵 Combined file data loaded');
                if (audioRef.current && !combinedFileLoadedRef.current) {
                  // Another fallback: try to get duration from onLoadedData
                  const duration = audioRef.current.duration;
                  if (!isNaN(duration) && isFinite(duration) && duration > 0) {
                    console.log(`🎵 Got duration from onLoadedData: ${duration}s`);
                    combinedFileLoadedRef.current = true;
                    setTotalAudioDuration(duration);
                    setAudioLoadingState('complete');
                    setAudioLoadingProgress(100);
                  }
                }
              }}
              onError={(e) => {
                console.error('❌ Error loading combined audio file:', e);
                const audio = e.currentTarget;
                if (audio.error) {
                  const errorMessages: { [key: number]: string } = {
                    1: 'MEDIA_ERR_ABORTED - Loading aborted',
                    2: 'MEDIA_ERR_NETWORK - Network error (may be CORS or authentication issue)',
                    3: 'MEDIA_ERR_DECODE - Decoding error',
                    4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - Source not supported'
                  };
                  const errorCodeMessage = errorMessages[audio.error.code] || `Error code ${audio.error.code}`;
                  console.error(`❌ Audio error: ${errorCodeMessage}: ${audio.error.message || 'Unknown error'}`);
                }
                setAudioLoadingState('complete');
                setTotalAudioDuration(0);
                setAudioLoadingProgress(100);
              }}
              onStalled={() => {
                console.warn('⚠️ Audio loading stalled for combined file');
              }}
              onProgress={() => {
                // Log progress to help debug
                if (audioRef.current && audioRef.current.buffered.length > 0) {
                  const buffered = audioRef.current.buffered.end(0);
                  const duration = audioRef.current.duration;
                  if (!isNaN(duration) && isFinite(duration) && duration > 0 && !combinedFileLoadedRef.current) {
                    console.log(`🎵 Progress event - buffered: ${buffered}s, duration: ${duration}s`);
                    combinedFileLoadedRef.current = true;
                    setTotalAudioDuration(duration);
                    setAudioLoadingState('complete');
                    setAudioLoadingProgress(100);
                  }
                }
              }}
              crossOrigin="anonymous"
              preload="auto"
              className="hidden"
            />
          )}
          
          {audioLoadingState === 'loading' ? (
            <div className="flex flex-col items-center">
              <h3 className="text-lg mb-2">Loading audio files... {audioLoadingProgress}%</h3>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-600 h-4 rounded-full"
                  style={{ width: `${audioLoadingProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Please wait while we prepare the audio playback...</p>
            </div>
          ) : audioLoadingState === 'complete' && totalAudioDuration === 0 ? (
            <div className="flex flex-col items-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ Audio Loading Failed</h3>
              <p className="text-sm text-yellow-700 mb-2">
                Unable to load audio files. This is likely due to:
              </p>
              <ul className="text-sm text-yellow-700 list-disc list-inside mb-4 text-left">
                <li>CORS restrictions - S3 bucket may not allow direct browser access</li>
                <li>Authentication required - URLs may need to be presigned</li>
                <li>Network connectivity issues</li>
              </ul>
              <p className="text-xs text-yellow-600">
                Check the browser console for detailed error messages. Audio playback features will be unavailable.
              </p>
            </div>
          ) : isCombinedFile ? (
            // Seamless playback for combined file
            <div className="flex flex-col items-center w-full">
              <div className="w-full mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <button
                    onClick={handlePlayPause}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    disabled={audioLoadingState !== 'complete'}
                  >
                    {isPlaying ? '⏸ Pause' : '▶ Play'}
                  </button>
                  <span className="text-sm text-gray-600">
                    {Math.floor(currentTime / 60)}:{(Math.floor(currentTime % 60)).toString().padStart(2, '0')} / {Math.floor(totalAudioDuration / 60)}:{(Math.floor(totalAudioDuration % 60)).toString().padStart(2, '0')}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={totalAudioDuration || 0}
                  value={currentTime}
                  onChange={(e) => handleSeek(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  disabled={audioLoadingState !== 'complete'}
                />
              </div>
              {audioLoadingState === 'complete' && totalAudioDuration > 0 && (
                <div className="text-sm text-gray-500">
                  Total duration: {Math.floor(totalAudioDuration / 60)}m {Math.round(totalAudioDuration % 60)}s
                </div>
              )}
            </div>
          ) : (
            // Chunked playback (multiple files)
            <div className="flex flex-col items-center">
              <audio
                ref={audioRef}
                src={recordings[currentRecordingIndex]?.url}
                onEnded={handleAudioEnded}
                controls
                className="w-full mb-4"
              />
              <div className="flex items-center space-x-4">
                <button
                  onClick={handlePlayPause}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                  disabled={audioLoadingState !== 'complete' || totalAudioDuration === 0}
                >
                  {isPlaying ? 'Pause' : 'Play All'}
                </button>
                <span className="text-gray-600">
                  Recording {currentRecordingIndex + 1} of {recordings.length}
                </span>
              </div>
              {audioLoadingState === 'complete' && totalAudioDuration > 0 && (
                <div className="text-sm text-gray-500 mt-2">
                  Total duration: {Math.floor(totalAudioDuration / 60)}m {Math.round(totalAudioDuration % 60)}s
                </div>
              )}
            </div>
          )}
        </div>
      )}
          </div>
        )}
      </div>

      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="flex justify-end items-center mb-4">
          <input
            type="checkbox"
            className="form-checkbox h-5 w-5 text-blue-600 mr-2"
            checked={showEvents}
            onChange={() => setShowEvents(!showEvents)}
            id="show-events-checkbox"
          />
          <label htmlFor="show-events-checkbox" className="text-gray-700 text-sm">
            Show events
          </label>
        </div>
        {timelineItems.map((item, index) => {
          if (item.type === 'event' && !showEvents) {
            return null;
          }
          if (item.type === 'transcript') {
            const part = item.data as TranscriptPart;
            const hasInteractions = isTimeInAnyInteraction(part.start_time);
            const matchingInteractions = getInteractionsForTime(part.start_time);

            // Only show buttons for interactions where this is the first transcript part
            const interactionsToShowButtons = matchingInteractions.filter(
              interaction => getFirstTranscriptPartForInteraction(interaction) === part.id
            );

            return (
              <div key={`transcript-${part.id}`} className="mb-2">
                {interactionsToShowButtons.length > 0 && (
                  <div className="flex flex-col space-y-1 mb-2">
                    {interactionsToShowButtons.map(interaction => {
                      const xp = gemByInteractionId[interaction.id];
                      return (
                      <div key={interaction.id} className="flex flex-col space-y-1">
                        <div className="flex items-center space-x-2">
                          <div className="flex space-x-1">
                            {interaction.correct_human_verified === null && (
                              <>
                                <button
                                  onClick={() => handleMarkInteraction(interaction.id, true)}
                                  className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200"
                                  title="Mark as correct"
                                >
                                  Correct
                                </button>
                                <button
                                  onClick={() => handleMarkInteraction(interaction.id, false)}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                                  title="Mark as incorrect"
                                >
                                  Incorrect
                                </button>
                              </>
                            )}
                            {interaction.correct_human_verified !== null && (
                              <div className="flex items-center space-x-2">
                                <span className={`text-xs font-medium ${interaction.correct_human_verified
                                  ? 'text-green-600'
                                  : 'text-red-600'
                                  }`}>
                                  {interaction.correct_human_verified
                                    ? 'Verified Correct'
                                    : 'Verified Incorrect'}
                                </span>
                                <button
                                  onClick={() => handleMarkInteraction(interaction.id, null)}
                                  className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                                  title="Remove marking"
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                          {interaction.interaction_type !== 'drink_promotion' && (
                            <span className="text-sm text-gray-600 font-mono">
                              {buildFeedbackDisplay(interaction.data)}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 capitalize">
                            {interaction.interaction_type}
                          </span>
                          <button
                            onClick={() => handleNextInteraction(interaction.start_time)}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                            title="Go to next interaction"
                          >
                            Next →
                          </button>
                          <div className="flex-1" />
                          {xp && xp.amount !== 0 && (
                            <span
                              className={`px-2 py-0.5 text-xs font-semibold rounded whitespace-nowrap ${
                                xp.amount > 0
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                              title={xp.description ?? undefined}
                            >
                              {xp.amount > 0 ? `+${xp.amount}` : `${xp.amount}`} XP
                            </span>
                          )}
                        </div>
                        {interaction.data?.reasoning && (
                          <div className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200">
                            {interaction.data.reasoning}
                          </div>
                        )}
                        {interaction.interaction_type === 'phrase_rule' && (() => {
                          // Explanation for a phrase_rule interaction, rendered as one
                          // naturally-wrapping paragraph (no bullets / no forced line
                          // breaks). Positive vs. negative polarity is distinguished with
                          // the same green/red treatment used elsewhere for phrase rules
                          // (see RestaurantAdmin.tsx). Every field is optional — omit
                          // gracefully rather than printing empty labels. See spec feat-154.
                          const data = interaction.data || {};
                          const polarity: string | undefined = data.polarity;
                          const isNegative = polarity === 'negative';
                          const isPositive = polarity === 'positive';
                          const heard: string | undefined = data.matched_text || data.phrase;
                          const tip: string | undefined = data.light_bulb_text;
                          const evidence: string | undefined = data.evidence;
                          if (!polarity && !heard && !tip && !evidence) {
                            return null;
                          }
                          const colorClass = isNegative
                            ? 'border-red-200 text-red-700'
                            : isPositive
                              ? 'border-green-200 text-green-700'
                              : 'border-gray-200 text-gray-600';
                          return (
                            <div className={`text-xs pl-2 border-l-2 ${colorClass}`}>
                              {polarity && (
                                <span className="font-semibold">
                                  {isNegative ? '💡 Coaching moment' : '✨ Nice phrasing'}{' — '}
                                </span>
                              )}
                              {heard && <>Heard: “{heard}”. </>}
                              {tip && <>{tip} </>}
                              {evidence && <span className="text-gray-600">{evidence}</span>}
                            </div>
                          );
                        })()}
                        {interaction.interaction_type === 'drink_promotion' && interaction.data?.drink_promoted && (
                          <div className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200">
                            Promoted: {interaction.data.drink_promoted}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-start">
                  {recordings.length > 0 && (
                    <div className="flex mr-2 mt-1 space-x-1">
                      <button
                        onClick={() => handlePlayFromTimestamp(part.start_time)}
                        className={`${audioLoadingState === 'complete' ? 'text-blue-500 hover:text-blue-700' : 'text-gray-300'} flex-shrink-0 p-1`}
                        disabled={audioLoadingState !== 'complete'}
                        title={audioLoadingState === 'complete'
                          ? `Play from ${formatTimestamp(part.start_time)}`
                          : 'Loading audio files...'
                        }
                      >
                        <FaPlay className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (audioRef.current && isPlaying) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          }
                        }}
                        className={`${isPlaying && audioLoadingState === 'complete' ? 'text-red-500 hover:text-red-700' : 'text-gray-300'} flex-shrink-0 p-1`}
                        disabled={!isPlaying || audioLoadingState !== 'complete'}
                        title="Pause playback"
                      >
                        <FaPause className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-gray-500 mr-2">
                      [{formatTimestamp(part.start_time)}]
                    </span>
                    <span
                      data-time={part.start_time}
                      className={`relative group ${hasInteractions ? 'bg-yellow-50 rounded px-1' : ''} ${highlightedStartTime === part.start_time ? 'ring-2 ring-blue-500 bg-blue-100 rounded px-1' : ''}`}
                    >
                      [{part.speaker}]: {part.text}
                    </span>
                    {hasInteractions && (
                      <div className="hidden group-hover:block absolute bg-white p-4 rounded shadow-lg z-10 max-w-4xl border border-gray-200 mt-2">
                        {matchingInteractions.map(interaction => (
                          <div key={interaction.id} className="text-sm text-gray-600 mb-2">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-semibold capitalize">{interaction.interaction_type}</span>
                            </div>
                            <pre className="mt-1 text-xs whitespace-pre-wrap bg-gray-50 p-2 rounded">
                              {JSON.stringify(interaction.data, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (item.type === 'order') {
            // Render Revel POS order card
            const order = item.data as RevelOrder;
            const hasAnyAttribution = order.items.some(i => i.interaction_links.length > 0);

            const matchRuleLabels: Record<string, string> = {
              drink_promotion: `\uD83E\uDD64 ${metricLabels.drink_promotion}`,
              metric_1: `\uD83E\uDD69 ${metricLabels.metric_1} Upsell`,
              metric_2: `\uD83C\uDF56 ${metricLabels.metric_2} Upsell`,
              metric_3: `\uD83C\uDF6B ${metricLabels.metric_3} Upsell`,
            };

            return (
              <div key={`order-${order.order_no}`} className="mb-4 border-l-4 border-green-500 pl-4 py-3 bg-green-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <span className="mr-2">{'\uD83E\uDDFE'}</span>
                    <span className="font-semibold text-green-800">Order #{order.order_no}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      [{formatTimestamp(order.shift_offset_seconds)}]
                    </span>
                  </div>
                  <span className="text-sm font-medium text-green-700">
                    ${order.final_total.toFixed(2)}
                  </span>
                </div>

                {order.items.length > 0 && (
                  <div className="space-y-1 ml-6">
                    {order.items.map(item => {
                      const isAttributed = item.interaction_links.length > 0;
                      return (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between text-sm px-2 py-1 rounded ${
                            isAttributed ? 'bg-yellow-100 border border-yellow-300' : ''
                          }`}
                        >
                          <div className="flex items-center">
                            <span className={isAttributed ? 'font-medium text-yellow-900' : 'text-gray-700'}>
                              {item.item_name}
                            </span>
                            {item.quantity > 1 && (
                              <span className="text-xs text-gray-500 ml-1">x{item.quantity}</span>
                            )}
                            {isAttributed && (
                              <span className="ml-2 text-xs text-yellow-700 font-medium">
                                {item.interaction_links.map(link =>
                                  matchRuleLabels[link.match_rule] || link.match_rule
                                ).join(', ')}
                              </span>
                            )}
                          </div>
                          {item.price != null && (
                            <span className="text-xs text-gray-500 ml-2">
                              ${item.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {hasAnyAttribution && (
                  <div className="mt-2 ml-6 space-y-1">
                    {order.items
                      .filter(i => i.interaction_links.length > 0)
                      .flatMap(i => i.interaction_links)
                      .map((link, idx) => (
                        <div key={idx} className="text-xs text-green-700 italic">
                          {link.match_explanation || 'Attributed to AI-detected upsell interaction'}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          } else {
            // Render shift event
            const event = item.data as ShiftEvent;

            return (
              <div key={`event-${event.id}`} className="mb-4 border-l-4 border-blue-400 pl-4 py-2 bg-blue-50 rounded">
                <div className="flex items-center mb-1">
                  <FaCalendarAlt className="text-blue-500 mr-2" />
                  <span className="font-medium text-blue-700">Event: {event.event_type}</span>
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {formatDate(event.created)}
                </div>
                <pre className="text-xs bg-white p-2 rounded border border-gray-200 overflow-auto max-h-60">
                  {formatEventData(event.event_data)}
                </pre>
              </div>
            );
          }
        })}
      </div>

      <ShiftFeedbackSection summary={shiftSummary} />

      {/* Single instance powering every metric-icon hover in the interaction
          rows. Label/description come from the already-cached per-shift config
          (metricDefByKey); status is carried per-icon in data-tooltip-content. */}
      <Tooltip
        id={metricTooltipId}
        place="top"
        opacity={1}
        style={{
          padding: 0,
          background: '#ffffff',
          color: '#1f2937',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 60,
          maxWidth: '320px',
        }}
        render={({ content }) => {
          if (!content) return null;
          let payload: { key: string; fulfilled: boolean };
          try {
            payload = JSON.parse(content);
          } catch {
            return null;
          }
          const { key, fulfilled } = payload;
          // Label from the per-shift config (criteriaLabels), falling back to the
          // raw key for criteria not present in the loaded config (AC3).
          const label = criteriaLabels[key] || key;
          const description = metricDefByKey[key]?.description;
          return (
            <div className="px-3 py-2 text-left font-sans">
              <div className="text-sm font-semibold text-gray-900">{label}</div>
              {description && (
                <div className="mt-0.5 text-xs text-gray-600 whitespace-normal">{description}</div>
              )}
              <div className="mt-1.5 text-xs font-medium">
                {fulfilled ? (
                  <span className="text-green-600">✅ Fulfilled</span>
                ) : (
                  <span className="text-gray-500">➖ Not fulfilled</span>
                )}
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

// ─── Shift feedback (post-shift summary) ────────────────────────────────
//
// Mirrors the "Training points from your shift" section on the Flutter
// post-shift screen (lib/screens/post_shift_screen.dart). Reads
// shift.summary_json — the same field the Flutter app receives over the
// shift_summary WebSocket message.

const ShiftFeedbackSection: React.FC<{ summary: ShiftSummaryJson | null }> = ({ summary }) => {
  const highlights = summary?.highlights ?? [];
  const areas = summary?.areas_to_improve ?? [];

  if (highlights.length === 0 && areas.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 bg-white shadow-md rounded-lg p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">
        Training points from your shift
      </h2>

      {highlights.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-700 mr-2" />
            <span className="text-sm font-semibold text-green-700">Highlights</span>
          </div>
          <div className="space-y-2">
            {highlights.map((h, i) => (
              <div
                key={`hl-${i}`}
                className="rounded-lg px-3 py-3"
                style={{ backgroundColor: 'rgba(47, 163, 95, 0.10)' }}
              >
                <div className="text-sm font-bold text-green-800">{h.title}</div>
                {h.reasoning && (
                  <div className="text-xs text-gray-700 mt-1 leading-snug">
                    {h.reasoning}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {areas.length > 0 && (
        <div>
          <div className="flex items-center mb-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-600 mr-2" />
            <span className="text-sm font-semibold text-yellow-700">Areas to work on</span>
          </div>
          <div className="space-y-2">
            {areas.map((a, i) => (
              <div
                key={`area-${i}`}
                className="rounded-lg px-3 py-3"
                style={{ backgroundColor: '#FFF7E0' }}
              >
                <div className="text-sm font-bold text-yellow-700">{a.title}</div>
                {a.suggestion ? (
                  <div className="text-sm italic text-gray-800 mt-1 leading-snug">
                    Try: &ldquo;{a.suggestion}&rdquo;
                  </div>
                ) : a.reasoning ? (
                  <div className="text-sm text-gray-800 mt-1 leading-snug">
                    {a.reasoning}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftTranscriptView;
